/**
 * Google Ads conversion pixel funnel fan-out.
 *
 * Previously this fired ad-hoc from the mobile-onboarding install page only (bootstrapping
 * its own gtag.js and calling `window.gtag('event', 'conversion', ...)` on render) — so any
 * signup that didn't pass through THAT specific screen (the desktop /onboarding v3 flow, the
 * extension's own Register screen, tms/loadhub onboarding) never reported a conversion to
 * Google Ads. That is why Ads showed clicks/spend with 0 recorded conversions: the only
 * working flow (mobile-onboarding) is a shrinking minority of total signups.
 *
 * This module mirrors metaPixel.ts's design: hook the SAME unified funnel chokepoint
 * (`fanOutFunnelEvent` in amplitude.ts) so every surface reports one consistent conversion,
 * instead of sprinkling `gtag()` calls across each onboarding page.
 */
import { FunnelEvent } from './funnel'

const GOOGLE_ADS_ID = 'AW-17700199828'
const CONVERSION_ID = 'AW-17700199828/G29xCKn-j-cbEJS7jvhB'

let scriptLoadStarted = false

/** Lazily injects the gtag.js loader for our Ads account, once, across the whole session. */
const ensureGtagLoaded = (): void => {
    if (typeof window === 'undefined' || scriptLoadStarted) return
    scriptLoadStarted = true

    window.dataLayer = window.dataLayer || []
    if (typeof window.gtag !== 'function') {
        window.gtag = function gtag(...args: unknown[]) {
            window.dataLayer.push(args)
        }
    }
    window.gtag('js', new Date())
    window.gtag('config', GOOGLE_ADS_ID)

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`
    document.head.appendChild(script)
}

/**
 * Mirrors a funnel event to the Google Ads conversion pixel, if it's the signup conversion.
 * No-ops outside the browser or for any event other than SIGNUP_COMPLETED — mirrors the same
 * "Регистрация(Amplituda)" conversion action Ads is already configured to expect.
 */
export const emitGoogleAdsFunnelEvent = (event: FunnelEvent): void => {
    if (typeof window === 'undefined') return
    if (event !== FunnelEvent.SIGNUP_COMPLETED) return

    try {
        ensureGtagLoaded()
        window.gtag('event', 'conversion', { send_to: CONVERSION_ID })
    } catch {
        /* Ads pixel must never break the app */
    }
}
